//@ts-check
import dayjs from 'dayjs';
import { CacheHolder } from './CacheHolder';
import { ExpireCacheHandler } from './ExpireCacheHandler';

/**
 * @typedef {object} Index
 * @prop {CacheHolder<string[]> | ExpireCacheHandler<string[]>} idsByKey
 * @prop {number} idSize
 * @prop {string} target
 */

/**
 * @template T
 */
export class IndexedCacheHandler {
  /**
   * @param {object} option
   * @param {string[]} option.primary
   * @param {Index[]} option.indexes
   * @param {number} option.startHour
   * @param {string} option.timeKey
   */
  constructor({ primary, indexes, startHour, timeKey }) {
    /** @type {CacheHolder<T>} */
    this._byId = new CacheHolder();
    /** @type {Map<string, number>} */
    this._refMap = new Map();
    /** @type {Map<string, Index>} */
    this._indexes = new Map();
    this._primary = primary;
    this._timeKey = timeKey;
    this._startHour = startHour;
    indexes.forEach(index => {
      if (index.idsByKey instanceof ExpireCacheHandler) {
        index.idsByKey._byId.on('expired', (ids) => {
          this._decrementRefCount(ids);
        });
      }
      this._indexes.set(index.target, index);
    });
  }

  /**
   * @param {T} data
   * @returns {string} 
   */
  _getId(data) {
    return this._primary.map(p => data[p]).join('+');
  }

  /**
   * @param {string[]} ids
   * @returns {T} 
   */
  _getOldest(ids) {
    // @ts-ignore
    return ids.reduce((oldest, id) => {
      const data = this._byId.get(id);
      if (oldest[this._timeKey] == null) {
        return { ...data };
      }

      if (oldest[this._timeKey] > data[this._timeKey]) {
        return { ...data };
      }

      return oldest;
    }, {});
  }

  /**
   * @param {string[]} ids 
   * @returns {string[]}
   */
  _sortByDate(ids) {
    const datas = ids
      .map(id => this._byId.get(id))
      .sort((a, b) => {
        if (a[this._timeKey] < b[this._timeKey]) {
          return 1;
        }

        if (a[this._timeKey] > b[this._timeKey]) {
          return -1;
        }

        return 0;
      });

    return datas.map(data => this._getId(data));
  }

  /**
   * @param {T} data
   * @returns {string} 
   */
  _getTimeKey(data) {
    if (data == null) {
      return null;
    }

    const date = data[this._timeKey];

    if (date == null) {
      return null;
    }

    if (String(date).length === 8) {
      return date;
    }

    const dDate = dayjs(date);
    if (dDate.hour() < this._startHour) {
      return dDate.subtract(1, 'day').format('YYYYMMDD');
    }

    return dDate.format('YYYYMMDD');
  }

  /**
   * @param {T | T[]} datas 
   */
  _incrementRefCount(datas) {
    [].concat(datas).forEach(data => {
      const id = this._getId(data);
      const org = this._byId.get(id);
      if (org == null) {
        this._refMap.set(id, 1);
        this._byId.set(id, data);
      } else {
        const oldRef = this._refMap.get(id);
        this._refMap.set(id, oldRef + 1);
        this._byId.set(id, data);
      }
    });
  }

  /** @param {string | string[]} ids */
  _decrementRefCount(ids) {
    [].concat(ids).forEach(id => {
      const oldRef = this._refMap.get(id);
      const ref = oldRef === 0 ? 0 : oldRef - 1;
      this._refMap.set(id, ref);
      if (ref === 0) {
        this._refMap.delete(id);
        this._byId.delete(id);
      }
    });
  }

  removeIndexData(indexKey, key) {
    const index = this._indexes.get(indexKey);
    const idsByKey = index.idsByKey;
    if (idsByKey.has(key)) {
      const ids = idsByKey.get(key);
      this._decrementRefCount(ids);
    }
    idsByKey.delete(key);
  }

  /**
   * @param {string} indexKey 
   * @param {string} key 
   * @param {T[]} datas 
   */
  initData(indexKey, key, datas) {
    datas = datas || [];
    const index = this._indexes.get(indexKey);
    const idsByKey = index.idsByKey;
    if (idsByKey.has(key)) {
      const ids = idsByKey.get(key);
      this._decrementRefCount(ids);
      idsByKey.delete(key);
    }

    const ids = datas.map(data => this._getId(data));
    if (ids.length > index.idSize) {
      return;
    }

    idsByKey.set(key, ids);
    this._incrementRefCount(datas);
  }

  /**
   * @param {T} data 
   */
  create(data) {
    this._indexes.forEach(index => {
      const idsByKey = index.idsByKey;
      const key = index.target === this._timeKey ? this._getTimeKey(data) : data[index.target];
      const ids = idsByKey.get(key);
      if (ids == null) {
        return;
      }

      const id = this._getId(data);
      if (ids.includes(id)) {
        this._byId.set(id, data);
        return;
      }

      ids.push(id);
      this._incrementRefCount(data);

      if (ids.length <= index.idSize) {
        const sortedIds = this._sortByDate(ids);
        idsByKey.set(key, sortedIds);
        return;
      }

      const oldest = this._getOldest(ids);
      const oldestId = this._getId(oldest);
      const newIds = ids.filter(id => id != oldestId);
      idsByKey.set(key, this._sortByDate(newIds));
      this._decrementRefCount(oldestId);
    });
  }

  /**
   * @param {string} id 
   * @param {T} diff 
   */
  update(id, diff, replace = false) {
    const data = this._byId.get(id);
    if (data == null) {
      return;
    }
    this._indexes.forEach(index => {
      const idsByKey = index.idsByKey;
      
      const key = index.target === this._timeKey ? this._getTimeKey(data) : data[index.target];
      const diffKey = index.target === this._timeKey ? this._getTimeKey(diff) : diff[index.target];
      if (diffKey == null || key == diffKey) {
        this._byId.set(id, replace ? diff : { ...data, ...diff });
        return;  
      }
      
      // 元のkeyのところを削除
      const orgIds = idsByKey.get(key);
      if (orgIds != null && orgIds.length >= index.idSize) {
        idsByKey.delete(key);
        this._decrementRefCount(orgIds);
      } else if (orgIds != null) {
        idsByKey.set(key, orgIds.filter(val => val != id));
        this._decrementRefCount(id);
      }

      // 変更先のkey(diffKey)
      const ids = idsByKey.get(diffKey);
      if (ids == null) {
        return;
      } 
      
      const newData = replace ? diff : { ...data, ...diff };
      if (ids.includes(id)) {
        this._byId.set(id, newData);
      } else {
        ids.push(id);
        this._incrementRefCount(newData);
      }


      if (ids.length <= index.idSize) {
        const sortedIds = this._sortByDate(ids);
        idsByKey.set(diffKey, sortedIds);
        return;
      }

      const oldest = this._getOldest(ids);
      const oldestId = this._getId(oldest);
      const newIds = ids.filter(id => id != oldestId);
      idsByKey.set(diffKey, this._sortByDate(newIds));
      this._decrementRefCount(oldestId);
    });
  }

  /**
   * @param {string} id 
   */
  delete(id) {
    const data = this._byId.get(id);
    if (data == null) {
      return;
    }

    this._indexes.forEach(index => {
      const idsByKey = index.idsByKey;
      const key = index.target === this._timeKey ? this._getTimeKey(data) : data[index.target];
      const ids = idsByKey.get(key);
      if (ids == null) {
        return;
      }

      if (ids.length >= index.idSize) {
        idsByKey.clear();
        this._decrementRefCount(ids);
        return;
      }

      const newIds = ids.filter(v => v !== id);
      idsByKey.set(key, newIds);
      this._decrementRefCount(id);
    });
  }

  /**
   * @param {T} data 
   */
  replace(data) {
    const id = this._getId(data);
    const org = this._byId.get(id);
    if (org == null) {
      this.create(data);
    } else {
      this.update(id, data, true);
    }
  }

  /** @param {string} key */
  get(key) {
    return this._byId.get(key);
  }

  /** @param {string} key */
  has(key) {
    return this._byId.has(key);
  }

  getAll() {
    return this._byId.keys().map(key => this._byId.get(key));
  }

  /**
   * @param {string} indexKey
   * @param {string} key 
   */
  getByIndex(indexKey, key) {
    const index = this._indexes.get(indexKey);
    if (index == null) {
      return null;
    }
    
    if (!index.idsByKey.has(key)) {
      return null;  
    }

    const ids = index.idsByKey.get(key);
    if (ids == null) {
      return null;
    }
    return ids.map(id => this._byId.get(id));
  }

  clear() {
    this._byId.clear();
    this._refMap.clear();
    this._indexes.forEach(index => {
      index.idsByKey.clear();
    });
  }
}

IndexedCacheHandler.Builder = class {
  constructor() {
    this._param = {
      primary: ['id'],
      startHour: 6,
      indexes: [],
      timeKey: 'time'
    };
  }

  setTimeKey(timeKey) {
    this._param.timeKey = timeKey;
    return this;
  }

  setPrimary(primary) {
    this._param.primary = primary;
    return this;
  }

  setStartHour(startHour) {
    this._param.startHour = startHour;
    return this;
  }

  addIndex(target, keySize, idSize) {
    this._param.indexes.push({
      idsByKey: new CacheHolder(keySize),
      idSize: idSize == null ? Number.MAX_VALUE : idSize,
      target
    });
    return this;
  }

  addExpireIndex(target, keySize, idSize, expireMS) {
    this._param.indexes.push({
      idsByKey: new ExpireCacheHandler(expireMS, keySize),
      idSize: idSize == null ? Number.MAX_VALUE : idSize,
      target
    });
    return this;
  }

  build() {
    return new IndexedCacheHandler(this._param);
  }
};
